import { createGlobalStyle } from 'styled-components'

import { useDesignMode } from 'src/design-mode/useDesignMode'

import { GEIST_FONT_FAMILY } from './font'

/**
 * The Geist half of the design toggle: the app's own text, everything that is
 * not an antd component.
 *
 * Ported on the same mechanism as the component seams (`src/antd/Button.tsx`,
 * `Space`, `Typography`) — one `useDesignMode()` dispatch, antd on either
 * side of it — because a font is not a component and has no seam of its own.
 * What differs is only what the two branches produce: a seam renders one
 * library or the other, this renders one global rule or NOTHING. With the
 * toggle off `createGlobalStyle` is never mounted, so not one declaration
 * reaches the page and today's `body` rule in `src/App/global.styles.ts` is
 * the whole answer, exactly as it is now.
 *
 * `html body`, not `body`, ON PURPOSE. The rule it has to beat is that same
 * `body` rule, and both are unlayered styled-components output — so
 * specificity decides, not injection order, which is the part
 * `createGlobalStyle` gives no guarantee about. `html body` is (0,0,2) against
 * (0,0,1) and wins by construction. Nothing else in the app styles `body`'s
 * font.
 *
 * It reaches antd's popups too — Modal, Dropdown, Select and Tooltip portal to
 * `<body>`, not to their trigger — but only for the text antd leaves to
 * inheritance. antd's own components carry `font-family` in their generated
 * CSS; `src/App/AntdI18n.tsx` is what switches those. See `./font.ts`.
 *
 * THE SMOOTHING PAIR RIDES ON THE SAME RULE, and unlike the family it needs no
 * second dispatch point: `-webkit-font-smoothing` and `-moz-osx-font-smoothing`
 * are INHERITED, and antd writes neither into any component's generated CSS,
 * so one declaration on `html body` reaches a Table cell, a Form label and a
 * portalled Modal alike. It asks macOS/Chrome for greyscale antialiasing
 * instead of the subpixel default, which is what shadcn's own baseline (and
 * essentially every Geist-based design) is drawn against — the subpixel
 * default renders text noticeably heavier, and that extra weight is half of
 * why the labels read wrong beside their icons. antd already asks for exactly
 * this pair on ICONS (`resetIcon()`, antd/es/style/index.js), so with the
 * toggle off the app antialiases its glyphs one way and its text another; this
 * rule is what makes the two agree, and it makes them agree only in design
 * mode. Whether the antd path wants it too is a real question, but it is an
 * app-wide typographic change with no toggle to hide behind, so it is NOT
 * decided here.
 *
 * `text-rendering` is deliberately absent. `optimizeLegibility` is not a
 * smoothing control — it turns on kerning and optional ligatures, and on long
 * text nodes it is a measured layout cost. antd sets it on icons, where the
 * node is one glyph; nothing here needs it.
 */
const GeistBodyFont = createGlobalStyle`
  html body {
    font-family: ${GEIST_FONT_FAMILY};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
`

export const AppFont = () => {
  const designMode = useDesignMode()

  if (designMode !== 'shadcn') return null

  return <GeistBodyFont />
}

export default AppFont
